import React, { useState } from "react";
import {
    Button, Collapse, IconButton, Switch, TextField, Tooltip, FormControlLabel
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import Langs, { Localize } from "../../app/lang/langs";
import styles from "../../containers/vresume/VResume.module.css";

/**
 * How many bullet lines one experience may carry.
 *
 * Must match `MAX_BULLETS` in the backend's `helpers/vresume.js`, which truncates the array
 * past it on save — a higher number here would let the user type lines that are silently
 * dropped.
 */
const MAX_BULLETS = 12

/**
 * V-Resume — one experience block.
 *
 * ── The Vital / manual distinction is the point of this component ─────────────────────
 *
 * A block with `source === 'vital'` was materialised from a `team_members` mission by
 * `VResumesController.syncVitalExperiences`. Vital already knows the client, the poste and
 * the dates; what it does not and cannot know is what the person actually *did*, which is
 * the only part a CV reader cares about. So the bullets are the collab's to write, and the
 * snapshot fields are theirs to correct — nothing is locked.
 *
 * They are not locked because the snapshot is a starting point, not a record: `postes.name`
 * is a scheduling label ("Agent de sécurité"), and on a CV going to a client the collab may
 * legitimately need "Agent de sécurité incendie SSIAP 1". Editing the block changes the CV
 * and never touches `team_members`.
 *
 * Removing a Vital block *excludes* it (`included = 0`) rather than deleting it, because the
 * mission still exists and the next sync would re-add a deleted one — the removal would
 * silently not stick. The confirm dialog in the parent says so; here the difference shows as
 * the badge and the dimmed styling.
 *
 * Purely presentational: every mutation goes out through `onChange` / `onRemove` /
 * `onRestore`, and the parent owns the debounced save.
 */
export default function ExperienceCard({
    experience,
    onChange,
    onRemove,
    onRestore,
    disabled = false,
    lang,
    /** Drag-and-drop reorder wiring, owned by the parent. */
    dragHandlers = {},
    isDropTarget = false,
    /** Open on mount — used for a block the user just added, so they can type straight
     *  into it instead of hunting for the row that appeared. */
    defaultExpanded = false
}) {

    const [expanded, setExpanded] = useState(defaultExpanded)

    const isVital = experience.source === 'vital'
    const bullets = experience.bullets || []

    const set = (key, value) => onChange({ [key]: value })

    const setBullet = (index, value) => {
        onChange({ bullets: bullets.map((bullet, i) => i === index ? value : bullet) })
    }

    const addBullet = () => {
        onChange({ bullets: [...bullets, ''] })
        setExpanded(true)
    }

    const removeBullet = (index) => {
        onChange({ bullets: bullets.filter((bullet, i) => i !== index) })
    }

    const atBulletCap = bullets.length >= MAX_BULLETS

    /**
     * Enter in the Contexte box moves what was typed down into the bullet list.
     *
     * This is the primary way bullets get written: type a line, press Enter, type the next.
     * The box clears and keeps focus, so several lines can be entered without ever reaching
     * for the mouse or the "Add a line" button.
     *
     * ── The details that make it behave ──────────────────────────────────────────────
     *
     * - **Shift+Enter still inserts a real newline.** Without an escape hatch the field
     *   could no longer hold a genuine two-line context, and Enter would be a trap rather
     *   than a shortcut.
     * - **The whole value is promoted, not the text before the cursor.** Splitting at the
     *   caret would be cleverer and less predictable; promoting everything is what the
     *   field visibly contains.
     * - **A multi-line value promotes as one bullet per line.** That is what makes pasting
     *   a list work: paste five lines, press Enter, get five bullets. Blank lines are
     *   dropped rather than becoming empty bullets.
     * - **Nothing is promoted at the cap**, and `preventDefault` still runs so the keypress
     *   cannot smuggle in a newline instead. The field keeps its text (nothing is lost) and
     *   the helper text below says why.
     * - **`bullets` and `description` go out in ONE `onChange`.** The parent debounces per
     *   experience and merges patches, so two calls would also work, but a single patch
     *   keeps the local optimistic update and the persisted write identical.
     */
    const onContextKeyDown = (event) => {
        if (event.key !== 'Enter' || event.shiftKey) return
        event.preventDefault()
        if (atBulletCap) return

        const lines = String(experience.description || '')
            .split(/\r?\n/)
            .map(line => line.trim())
            .filter(Boolean)
        // Enter on an empty box is a no-op rather than a blank bullet.
        if (!lines.length) return

        const room = MAX_BULLETS - bullets.length
        const promoted = lines.slice(0, room)
        // Anything that did not fit stays in the box, so a paste larger than the cap loses
        // nothing — the user can trim it and press Enter again.
        const leftover = lines.slice(room)

        onChange({
            bullets: [...bullets, ...promoted],
            description: leftover.join('\n')
        })
        setExpanded(true)
    }

    /** The one-line summary shown when the card is collapsed. */
    const meta = [
        experience.client,
        experience.company,
        experience.location
    ].filter(Boolean).join(' · ')

    // A date input needs `YYYY-MM-DD`; the API returns a full timestamp for a DATE column on
    // some driver paths. Slicing rather than parsing avoids a timezone shift moving the day.
    const dateValue = (value) => value ? String(value).slice(0, 10) : ''

    return (
        <div
            className={`${styles.experience}`
                + (experience.included ? '' : ` ${styles.experienceExcluded}`)
                + (isDropTarget ? ` ${styles.dropTarget}` : '')}
            {...(disabled ? {} : dragHandlers)}
        >
            <div className={styles.experienceHeader} onClick={() => setExpanded(!expanded)}>

                {!disabled
                    ? <span className={styles.experienceGrip}
                        // The grip stops the click from also toggling the card: a drag that
                        // starts and ends in place would otherwise collapse the block the
                        // user was trying to move.
                        onClick={(event) => event.stopPropagation()}>
                        <DragIndicatorIcon sx={{ fontSize: 18 }} />
                    </span>
                    : null}

                <div className={styles.experienceHeading}>
                    <div className={styles.experienceTitle}>
                        {experience.poste || Localize("VRESUME_EXP_UNTITLED", lang)}
                    </div>
                    {meta ? <div className={styles.experienceMeta}>{meta}</div> : null}
                </div>

                <span className={`${styles.sourceBadge}`
                    + (isVital ? '' : ` ${styles.sourceBadgeManual}`)}>
                    {isVital ? <Langs str="VRESUME_SOURCE_VITAL" /> : <Langs str="VRESUME_SOURCE_MANUAL" />}
                </span>

                {!disabled
                    ? <Tooltip
                        title={Localize(experience.included
                            ? "VRESUME_EXP_INCLUDED_ON" : "VRESUME_EXP_INCLUDED_OFF", lang)}
                        arrow>
                        <span onClick={(event) => event.stopPropagation()}>
                            <Switch
                                size="small"
                                checked={!!experience.included}
                                onChange={(event) => set('included', event.target.checked)}
                            />
                        </span>
                    </Tooltip>
                    : null}

                {!disabled
                    ? <Tooltip
                        title={Localize(isVital
                            ? "VRESUME_EXP_EXCLUDE_TOOLTIP" : "VRESUME_EXP_DELETE_TOOLTIP", lang)}
                        arrow>
                        <IconButton size="small"
                            onClick={(event) => {
                                event.stopPropagation()
                                // A Vital block that is already excluded has nothing left to
                                // remove — the button restores it instead, so the toggle is
                                // reversible from the same place it was used.
                                if (isVital && !experience.included && onRestore) return onRestore()
                                onRemove()
                            }}>
                            <DeleteOutlineIcon sx={{ fontSize: 18, color: 'var(--red)' }} />
                        </IconButton>
                    </Tooltip>
                    : null}

                <IconButton size="small" onClick={(event) => {
                    event.stopPropagation()
                    setExpanded(!expanded)
                }}>
                    {expanded
                        ? <ExpandLessIcon sx={{ fontSize: 20, color: '#8a8f9c' }} />
                        : <ExpandMoreIcon sx={{ fontSize: 20, color: '#8a8f9c' }} />}
                </IconButton>
            </div>

            <Collapse in={expanded} unmountOnExit>
                <div className={styles.experienceBody}>

                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                        <TextField
                            size="small" disabled={disabled}
                            label={<Langs str="VRESUME_EXP_POSTE" />}
                            value={experience.poste || ''}
                            onChange={(event) => set('poste', event.target.value)}
                            sx={{ flex: '1 1 200px' }}
                            InputProps={{ sx: { fontFamily: 'roboto', fontSize: 13 } }}
                            InputLabelProps={{ sx: { fontFamily: 'roboto', fontSize: 13 } }}
                        />
                        <TextField
                            size="small" disabled={disabled}
                            label={<Langs str="VRESUME_EXP_CLIENT" />}
                            value={experience.client || ''}
                            onChange={(event) => set('client', event.target.value)}
                            sx={{ flex: '1 1 180px' }}
                            InputProps={{ sx: { fontFamily: 'roboto', fontSize: 13 } }}
                            InputLabelProps={{ sx: { fontFamily: 'roboto', fontSize: 13 } }}
                        />
                        <TextField
                            size="small" disabled={disabled}
                            label={<Langs str="VRESUME_EXP_COMPANY" />}
                            value={experience.company || ''}
                            onChange={(event) => set('company', event.target.value)}
                            sx={{ flex: '1 1 160px' }}
                            InputProps={{ sx: { fontFamily: 'roboto', fontSize: 13 } }}
                            InputLabelProps={{ sx: { fontFamily: 'roboto', fontSize: 13 } }}
                        />
                        <TextField
                            size="small" disabled={disabled}
                            label={<Langs str="VRESUME_EXP_LOCATION" />}
                            value={experience.location || ''}
                            onChange={(event) => set('location', event.target.value)}
                            sx={{ flex: '1 1 140px' }}
                            InputProps={{ sx: { fontFamily: 'roboto', fontSize: 13 } }}
                            InputLabelProps={{ sx: { fontFamily: 'roboto', fontSize: 13 } }}
                        />
                    </div>

                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12, alignItems: 'center' }}>
                        <TextField
                            size="small" type="date" disabled={disabled}
                            label={<Langs str="VRESUME_EXP_START" />}
                            value={dateValue(experience.start_date)}
                            onChange={(event) => set('start_date', event.target.value)}
                            sx={{ flex: '0 1 165px' }}
                            InputProps={{ sx: { fontFamily: 'roboto', fontSize: 13 } }}
                            InputLabelProps={{ shrink: true, sx: { fontFamily: 'roboto', fontSize: 13 } }}
                        />
                        <TextField
                            size="small" type="date"
                            // A current role has no end date by definition, and the backend
                            // nulls the column when is_current is set. Disabling the input
                            // makes that visible instead of letting a value be typed and
                            // silently dropped on save.
                            disabled={disabled || !!experience.is_current}
                            label={<Langs str="VRESUME_EXP_END" />}
                            value={experience.is_current ? '' : dateValue(experience.end_date)}
                            onChange={(event) => set('end_date', event.target.value)}
                            sx={{ flex: '0 1 165px' }}
                            InputProps={{ sx: { fontFamily: 'roboto', fontSize: 13 } }}
                            InputLabelProps={{ shrink: true, sx: { fontFamily: 'roboto', fontSize: 13 } }}
                        />
                        <FormControlLabel
                            control={<Switch size="small" disabled={disabled}
                                checked={!!experience.is_current}
                                onChange={(event) => set('is_current', event.target.checked)} />}
                            label={<span style={{ fontFamily: 'roboto', fontSize: 12.5 }}>
                                <Langs str="VRESUME_EXP_CURRENT" />
                            </span>}
                        />
                    </div>

                    <TextField
                        size="small" fullWidth multiline minRows={2} disabled={disabled}
                        label={<Langs str="VRESUME_EXP_DESCRIPTION" />}
                        placeholder={Localize("VRESUME_EXP_DESCRIPTION_PLACEHOLDER", lang)}
                        value={experience.description || ''}
                        onChange={(event) => set('description', event.target.value)}
                        onKeyDown={disabled ? undefined : onContextKeyDown}
                        helperText={atBulletCap
                            ? Localize("VRESUME_EXP_BULLETS_FULL", lang)
                                .replace("%count%", MAX_BULLETS)
                            : Localize("VRESUME_EXP_CONTEXT_ENTER_HINT", lang)}
                        sx={{ mt: 1.5 }}
                        InputProps={{ sx: { fontFamily: 'roboto', fontSize: 13 } }}
                        InputLabelProps={{ sx: { fontFamily: 'roboto', fontSize: 13 } }}
                        FormHelperTextProps={{
                            sx: {
                                fontFamily: 'roboto', fontSize: 11, ml: 0.5,
                                color: atBulletCap ? 'var(--red)' : undefined
                            }
                        }}
                    />

                    {/* ── Bullet points ─────────────────────────────────────── */}

                    <p className={styles.sectionHint} style={{ marginTop: 14, marginBottom: 6 }}>
                        <Langs str="VRESUME_EXP_BULLETS_HINT" />
                    </p>

                    {bullets.length === 0
                        ? <div className={styles.emptyRows}><Langs str="VRESUME_EXP_NO_BULLETS" /></div>
                        : (
                            <div className={styles.rowList}>
                                {bullets.map((bullet, index) => (
                                    <div className={styles.row} key={index}>
                                        <TextField
                                            size="small" fullWidth multiline disabled={disabled}
                                            value={bullet}
                                            onChange={(event) => setBullet(index, event.target.value)}
                                            placeholder={Localize("VRESUME_EXP_BULLET_PLACEHOLDER", lang)}
                                            InputProps={{ sx: { fontFamily: 'roboto', fontSize: 13 } }}
                                        />
                                        {!disabled
                                            ? <IconButton size="small" onClick={() => removeBullet(index)}
                                                sx={{ mt: 0.5 }}>
                                                <DeleteOutlineIcon sx={{ fontSize: 18, color: 'var(--red)' }} />
                                            </IconButton>
                                            : null}
                                    </div>
                                ))}
                            </div>
                        )}

                    {!disabled && !atBulletCap
                        ? <Button size="small" startIcon={<AddIcon />} onClick={addBullet}
                            sx={{
                                mt: 1, textTransform: 'none', fontFamily: 'roboto',
                                fontSize: 12.5, color: 'var(--orange)'
                            }}>
                            <Langs str="VRESUME_EXP_ADD_BULLET" />
                        </Button>
                        : null}
                </div>
            </Collapse>
        </div>
    )
}
